// src/SideWinder.Web/src/composables/useAuth.ts
import { ref, computed } from 'vue'
import { api, isLoginVerificationPending, type UserProfile, type LoginVerificationPending } from '../api/client'
import { useViewAs } from './useViewAs'

const user = ref<UserProfile | null>(null)
const loading = ref(true)
const initialized = ref(false)
let _goToLogin: (() => void) | null = null

// Linked users Task 9 fix round 2 (re-review Minor 2): a view-as target
// left in sessionStorage from a previous superuser session in this browser
// profile — or one that survives a same-tab re-sign-in as someone else,
// with no intervening logout() or full reload — must never keep driving
// request()'s header or the Account panel's HL card for whoever `user` now
// resolves to. The switcher/banner are already gated on isSuperUser, but
// request() reads the target unconditionally. Called from every path that
// sets `user` after a successful sign-in (init, login, verifyCode), not
// just init() — round 1 only covered init(), which short-circuits after
// its first run and so never re-fires for a same-tab re-login.
function clearViewAsUnlessSuperUser() {
  if (user.value?.role !== 'superuser') useViewAs().clear()
}

// Identity pill bug fix 1 (2026-09-29): "the escape route must never
// vanish". init()'s /me call can fail for two very different reasons —
// the server confirming the caller is signed out (401) or not a superuser
// (403), versus a network blip / 5xx that tells us NOTHING about who's
// signed in. Only the former may clear an active view-as target: a
// confirmed 401/403 is the same signal as /me resolving to a non-superuser
// profile. A network/5xx failure must leave the target (and therefore the
// amber pill + stripe + Back-to-me escape) exactly as it was — request()
// (client.ts) attaches `.status` to the thrown Error for exactly this check.
function isConfirmedNotSuperUser(err: unknown): boolean {
  const status = (err as { status?: number } | null | undefined)?.status
  return status === 401 || status === 403
}

export function useAuth() {
  const isAuthenticated = computed(() => user.value !== null)
  const isSuperUser = computed(() => user.value?.role === 'superuser')
  // V118 (linked users): read-only viewer account (Paolo's linked
  // daughters) — Task 9 hides write controls in the UI on this flag.
  const isViewer = computed(() => user.value?.role === 'viewer')

  async function init() {
    if (initialized.value) return
    loading.value = true
    try {
      user.value = await api.authMe()
      clearViewAsUnlessSuperUser()
    } catch (e) {
      user.value = null
      // Only a CONFIRMED 401/403 clears the target here — see
      // isConfirmedNotSuperUser above. A network/5xx failure keeps it.
      if (isConfirmedNotSuperUser(e)) useViewAs().clear()
    } finally {
      loading.value = false
      initialized.value = true
    }
  }

  /**
   * Phase 1 of sign-in. Resolves to null once signed in, or to the pending
   * marker when HL has texted a verification code (2026-09-21 SMS step) —
   * the caller then collects the code and calls verifyCode().
   */
  async function login(
    email: string, hlUserId?: string, dob?: string, password?: string, secureNumber?: string,
  ): Promise<LoginVerificationPending | null> {
    const res = await api.authLogin(email, hlUserId, dob, password, secureNumber)
    if (isLoginVerificationPending(res)) return res
    user.value = res
    clearViewAsUnlessSuperUser()
    return null
  }

  /**
   * Phase 2: submit the SMS code HL sent. Signs the user in on success.
   * hlUserId (V118, linked users) disambiguates when the email is shared —
   * passed through unchanged from the phase-1 login attempt.
   */
  async function verifyCode(email: string, code: string, hlUserId?: string) {
    user.value = await api.authLoginVerify(email, code, hlUserId)
    clearViewAsUnlessSuperUser()
  }

  /** Ask HL to text a fresh code for the parked sign-in. */
  async function resendCode(email: string, hlUserId?: string): Promise<LoginVerificationPending> {
    return api.authLoginResend(email, hlUserId)
  }

  async function logout() {
    await api.authLogout()
    user.value = null
    // Linked users Task 9 ruling 5: a stale view-as target must never
    // survive a sign-out — the next sign-in (as anyone) must never
    // silently inherit someone else's data scope.
    useViewAs().clear()
  }

  function setGoToLogin(fn: () => void) { _goToLogin = fn }
  function goToLogin() { _goToLogin?.() }

  return { user, loading, isAuthenticated, isSuperUser, isViewer, init, login, verifyCode, resendCode, logout, setGoToLogin, goToLogin }
}
